import React from "react";
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import ChartTooltip from "../../../Components/Charts/ChartTooltip/ChartTooltip";
import AutoSizer from "react-virtualized-auto-sizer";
import * as R from "ramda";
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE,
} from "../../../Components/Charts/ChartConstants";
import { headerFormatter, useDynamicYAxisWidth } from "../../../Components/Charts/ChartUtils";
import "./Spend.scss";
import { useCompanyInfo } from "../../../redux/company";
import { dateToMddyy } from "../../../utils/date-utils";

interface SpendProps {
  data: any[];
  dateGrouping: string;
  syncId?: number;
  tooltipFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
}

const Spend: React.FC<SpendProps> = ({
  data,
  dateGrouping,
  syncId,
  tooltipFormatter,
  yAxisFormatter,
}) => {
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const { yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    data: data,
  });

  return (
    <div className="spendChartWrapper">
      <div className="spendChart">
        <AutoSizer>
          {({ width, height }) => (
            <AreaChart
              width={width}
              height={height}
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              syncId={syncId}
              ref={setChartRef}
            >
              <XAxis dataKey="date" tick={TICK_STYLE} tickFormatter={val => dateToMddyy(val)} />
              <YAxis
                tick={TICK_STYLE}
                tickFormatter={yAxisFormatter}
                tickLine={false}
                axisLine={false}
                width={yAxisWidth}
              />
              <CartesianGrid
                stroke={CARTESIAN_GRID_STROKE}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              <Area
                dataKey="spend"
                fill="#8254ff"
                isAnimationActive={false}
                stroke="#8254ff"
                type="monotone"
                fillOpacity={1}
              />
              <Tooltip
                content={({ label, payload }) => {
                  if (!payload || !label) {
                    return null;
                  }
                  const formattedLabel = headerFormatter(dateGrouping, label, fiscalCal);
                  let items = R.sortBy(
                    (item: any) => -Math.abs(item.value),
                    payload?.map(item => ({
                      label: (item?.name ?? "").toString().replace(/^./, c => c.toUpperCase()),
                      value: tooltipFormatter
                        ? tooltipFormatter(item.value)
                        : (item.value as number),
                      color: item.stroke,
                    })) || []
                  );

                  return <ChartTooltip headerLabel={formattedLabel} items={items} />;
                }}
                isAnimationActive={false}
              />
            </AreaChart>
          )}
        </AutoSizer>
      </div>
      <div className="rightOfChart">
        <div className="legend">
          <div className="legendItem">
            <div className="square" style={{ backgroundColor: "#8254ff" }} />
            <div>Spend</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Spend;
